import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ExternalLink, Eye, Smartphone, Monitor } from 'lucide-react';

interface WidgetConfig {
  id: string;
  organization_id: string;
  name: string;
  domain_whitelist: string[] | null;
  custom_css: Record<string, any> | null;
  branding_config: Record<string, any> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface WidgetToken {
  id: string;
  token: string;
  domain_whitelist: string[] | null;
  is_active: boolean;
  created_at: string;
}

interface WidgetPreviewProps {
  widget: WidgetConfig | null;
  tokens: WidgetToken[];
  organizationId?: string;
}

export const WidgetPreview: React.FC<WidgetPreviewProps> = ({
  widget,
  tokens,
  organizationId,
}) => {
  const [selectedToken, setSelectedToken] = React.useState<string>(
    tokens.length > 0 ? tokens[0].token : ''
  );
  const [previewMode, setPreviewMode] = React.useState<'desktop' | 'mobile'>('desktop');

  React.useEffect(() => {
    if (tokens.length > 0 && !selectedToken) {
      setSelectedToken(tokens[0].token);
    }
  }, [tokens, selectedToken]);

  const getPreviewStyles = () => {
    if (!widget?.branding_config) return {};
    
    const branding = widget.branding_config;
    return {
      '--primary-color': branding.primaryColor || '#3b82f6',
      '--secondary-color': branding.secondaryColor || '#64748b',
      '--font-family': branding.fontFamily || 'Inter',
      '--border-radius': `${branding.borderRadius || 8}px`,
    } as React.CSSProperties;
  };

  if (tokens.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Widget Preview
          </CardTitle>
          <CardDescription>
            Generate a token first to preview your widget
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
            <p className="text-muted-foreground">
              No tokens available for preview. Generate a token in the "Tokens & Security" tab.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Widget Preview
          </CardTitle>
          <CardDescription>
            See how your widget will appear on your website
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Preview Controls */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Token:</label>
                <Select value={selectedToken} onValueChange={setSelectedToken}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select token" />
                  </SelectTrigger>
                  <SelectContent>
                    {tokens.map((token) => (
                      <SelectItem key={token.id} value={token.token}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">
                            {token.token.substring(0, 20)}...
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {token.domain_whitelist?.length ? 'Domain Limited' : 'Any Domain'}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={previewMode === 'desktop' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreviewMode('desktop')}
              >
                <Monitor className="h-4 w-4 mr-2" />
                Desktop
              </Button>
              <Button
                variant={previewMode === 'mobile' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreviewMode('mobile')}
              >
                <Smartphone className="h-4 w-4 mr-2" />
                Mobile
              </Button>
            </div>
          </div>

          {/* Preview Frame */}
          <div className="border rounded-lg overflow-hidden bg-gray-50">
            <div className="bg-white p-4 border-b">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <div className="ml-4 text-sm text-gray-500">
                  {previewMode === 'desktop' ? 'Desktop Preview' : 'Mobile Preview'}
                </div>
              </div>
            </div>
            
            <div 
              className={`bg-white p-6 transition-all duration-300 ${
                previewMode === 'mobile' ? 'max-w-sm mx-auto' : ''
              }`}
              style={getPreviewStyles()}
            >
              {/* Simulated Website Content */}
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Welcome to Our Dance Studio
                </h2>
                <p className="text-gray-600">
                  Discover our amazing classes and enroll today!
                </p>
              </div>

              {/* Widget Preview Container */}
              <div 
                id="hive360-widget-preview"
                className="border-2 border-dashed border-blue-200 rounded-lg p-6 bg-blue-50"
                style={getPreviewStyles()}
              >
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
                    <Eye className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Widget Style Preview
                  </h3>
                  <p className="text-sm text-gray-600 mb-1">
                    Sample formatting with your customized styling
                  </p>
                  <p className="text-xs text-blue-600 mb-4 font-medium">
                    ↓ SAMPLE DATA - NOT YOUR ACTUAL CLASSES ↓
                  </p>
                  
                  {/* Mock Widget Content */}
                  <div className="space-y-3 text-left">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium">Ballet for Beginners</h4>
                        <Badge variant="secondary">$45</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Perfect for dancers just starting their ballet journey
                      </p>
                      <Button 
                        size="sm" 
                        className="w-full"
                        style={{
                          backgroundColor: widget?.branding_config?.primaryColor || '#3b82f6',
                          borderRadius: `${widget?.branding_config?.borderRadius || 8}px`,
                        }}
                      >
                        Enroll Now
                      </Button>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium">Hip Hop Dance</h4>
                        <Badge variant="secondary">$50</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Learn the latest hip hop moves and techniques
                      </p>
                      <Button 
                        size="sm" 
                        className="w-full"
                        style={{
                          backgroundColor: widget?.branding_config?.primaryColor || '#3b82f6',
                          borderRadius: `${widget?.branding_config?.borderRadius || 8}px`,
                        }}
                      >
                        Enroll Now
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Preview Notice */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-400 text-center italic">
                  This is a preview with sample data to show widget formatting
                </p>
              </div>
            </div>
          </div>

          {/* Preview Information */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-2">About This Preview</h4>
            <div className="text-sm text-green-700 space-y-1">
              <p>• Shows how your styling customizations will appear</p>
              <p>• Uses sample data for formatting preview only</p>
              <p>• Changes to colors, fonts, and layout are reflected here</p>
              <p>• For live testing with real data, use the "Testing & Integration" tab</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};